import { useEffect, useMemo, useRef, useState } from "react";
import { BufferAttribute, BufferGeometry, Color, DoubleSide } from "three";
import type { PlayerSnapshot } from "@games-monorepo/yak-trails-core";

interface TerritoryMeshData {
  ownerId: string;
  positions: Float32Array;
  indices: Uint32Array;
  topIndexCount: number;
}

interface TerritoryWorkerMessage {
  type: "meshes";
  generation: number;
  meshes: TerritoryMeshData[];
}

function RaisedTerritory({ data, color }: { data: TerritoryMeshData; color: string }) {
  const geometry = useMemo(() => {
    const next = new BufferGeometry();
    next.setAttribute("position", new BufferAttribute(data.positions, 3));
    next.setIndex(new BufferAttribute(data.indices, 1));
    next.addGroup(0, data.topIndexCount, 0);
    next.addGroup(data.topIndexCount, data.indices.length - data.topIndexCount, 1);
    next.computeVertexNormals();
    next.computeBoundingSphere();
    return next;
  }, [data]);
  const sideColor = useMemo(() => new Color(color).offsetHSL(0, -0.04, -0.16), [color]);
  useEffect(() => () => geometry.dispose(), [geometry]);
  if (data.indices.length === 0) return null;
  return (
    <mesh geometry={geometry} receiveShadow>
      <meshStandardMaterial attach="material-0" color={color} roughness={0.76} metalness={0.01} polygonOffset polygonOffsetFactor={-1} />
      <meshStandardMaterial attach="material-1" color={sideColor} roughness={0.82} side={DoubleSide} />
    </mesh>
  );
}

export function TerritoryMeshes({
  owners,
  players,
  workerUrl,
}: {
  owners: Array<string | null>;
  players: PlayerSnapshot[];
  workerUrl?: string;
}) {
  const workerRef = useRef<Worker | null>(null);
  const previousOwnersRef = useRef<Array<string | null>>([]);
  const generationRef = useRef(0);
  const appliedGenerationRef = useRef(0);
  const [meshes, setMeshes] = useState<Map<string, TerritoryMeshData>>(() => new Map());
  const colorById = useMemo(() => new Map(players.map((player) => [player.id, player.color])), [players]);

  useEffect(() => {
    previousOwnersRef.current = [];
    generationRef.current = 0;
    appliedGenerationRef.current = 0;
    setMeshes(new Map());
    const worker = workerUrl
      ? new Worker(workerUrl, { type: "module" })
      : new Worker(new URL("../workers/territory.worker.ts", import.meta.url), { type: "module" });
    workerRef.current = worker;
    worker.onmessage = (message: MessageEvent<TerritoryWorkerMessage>) => {
      if (message.data.type !== "meshes" || message.data.generation <= appliedGenerationRef.current) return;
      appliedGenerationRef.current = message.data.generation;
      setMeshes((current) => {
        const next = new Map(current);
        for (const mesh of message.data.meshes) {
          if (mesh.indices.length === 0) next.delete(mesh.ownerId);
          else next.set(mesh.ownerId, mesh);
        }
        return next;
      });
    };
    return () => {
      worker.terminate();
      workerRef.current = null;
    };
  }, [workerUrl]);

  useEffect(() => {
    const worker = workerRef.current;
    if (!worker || owners.length === 0) return;
    const previous = previousOwnersRef.current;
    const affected = new Set<string>();
    const count = Math.max(previous.length, owners.length);
    for (let index = 0; index < count; index += 1) {
      const before = previous[index] ?? null;
      const after = owners[index] ?? null;
      if (before === after) continue;
      if (before) affected.add(before);
      if (after) affected.add(after);
    }
    previousOwnersRef.current = owners;
    if (affected.size === 0) return;
    generationRef.current += 1;
    worker.postMessage({
      type: "build",
      generation: generationRef.current,
      owners,
      affectedOwnerIds: [...affected].sort(),
    });
  }, [owners]);

  return (
    <group>
      {[...meshes.values()].map((mesh) => (
        <RaisedTerritory key={mesh.ownerId} data={mesh} color={colorById.get(mesh.ownerId) ?? "#a5f3dc"} />
      ))}
    </group>
  );
}
